/**
 * Audience Segment Types (ENG-1223 B4)
 *
 * An audience segment is a first-class, persisted definition: a named predicate
 * over screening answers (cross-question capable) that optionally carries a
 * quota target. It is decoupled from any single question - the quota engine
 * (services/quota) reads these definitions and enforces them. The DB column
 * that stores `config` is untyped jsonb; the shapes below are the authoritative
 * contract the API casts to on read.
 */

import { matchesConditionalRule, type SkipCondition } from "./screening-guide";

/**
 * Quota types control how segment target counts are enforced:
 * - EXACT: fill exactly N (segments must sum to total); this is the backward-compatible default
 * - MINIMUM: at least N from this group (floor, dynamically enforced)
 * - MAXIMUM: at most N from this group (cap, screen out when full)
 */
export const QuotaType = {
  EXACT: "EXACT",
  MINIMUM: "MINIMUM",
  MAXIMUM: "MAXIMUM",
} as const;

export type QuotaType = (typeof QuotaType)[keyof typeof QuotaType];

/**
 * A first-class audience segment's membership rule (ENG-1223 B4). Reuses the
 * screening condition primitive, so a segment can span questions (interlocking
 * cross-tab) or group options within one (OR). Unlike a screening
 * consistency rule it has no outcome and no self-check: every condition names
 * an explicit `questionId`.
 */
export interface SegmentPredicate {
  conditions: SkipCondition[];
  joinMethod: "and" | "or";
}

/**
 * The jsonb `config` stored on an `AudienceSegment` row. The DB column is
 * untyped (the database package can't import this contract); this is the
 * authoritative shape and the API casts to it on read.
 */
export interface AudienceSegmentConfig {
  predicate: SegmentPredicate;
  /**
   * How this segment was created. "OPTION" segments are materialized from a
   * per-option quota and fully reconciled from the guide on every save;
   * "AUTHORED" (or absent) segments are first-class cross-question definitions
   * the sync must never touch. Lets one table hold both without the save-time
   * reconcile clobbering authored segments.
   */
  source?: "OPTION" | "AUTHORED";
  /** Quota target; absent = a label-only segment (routing/priority, no cap). */
  target?: number;
  quotaType?: QuotaType;
  /**
   * Priority-mode grouping key. Cells of one interlocking cross-tab share a
   * key so their unfilled needs are summed as a single dimension. Absent =
   * the segment is its own dimension.
   */
  dimensionKey?: string;
}

/**
 * A loaded segment definition (row id + label + parsed config), the runtime
 * shape the quota engine works with.
 */
export interface AudienceSegmentDefinition {
  id: string;
  label: string;
  config: AudienceSegmentConfig;
}

// A questionId no real question can have, so reusing the consistency-rule
// matcher never mistakes a segment condition for a self-check.
const SEGMENT_PREDICATE_SELF = "__segment_self__";

/**
 * Whether a participant's answers satisfy a segment predicate. Reuses
 * `matchesConditionalRule`: a condition on an unanswered question resolves to
 * not-matched (presence gate) and an empty predicate never matches.
 */
export function matchesSegmentPredicate(
  predicate: SegmentPredicate,
  answers: Record<string, string[]>,
): boolean {
  return matchesConditionalRule(predicate, answers, SEGMENT_PREDICATE_SELF);
}
